// MARK://dhk8/pos/lib/api
// Thin fetch wrapper for the POS PWA. Backends are reached at
// `${apiBase()}/dhk8-<service>/v1/...` — i.e. `https://api.dockethub.app/v1/dhk8-<svc>/...`
// in cloud-direct mode, or `${edgeOrigin}/v1/dhk8-<svc>/...` in lan-relay mode. The
// per-service path segment maps to the ingress route `/v1/dhk8-<svc>(/|$)(.*)` which
// strips the `/v1/dhk8-<svc>` prefix before the service sees the request. Mirror of
// apps/admin-shell/src/lib/api.ts's `svcUrl` helper.
//
// Context (S1-Y hardening 2026-05-24):
//   - JWT bearer ← cookie `dhk8_token` HttpOnly+Secure+SameSite=Lax set by
//     dhk8-platform-auth (via @dockethub/sdk-auth setAuthCookie). The browser
//     auto-attaches it on `credentials:'include'`. JS can't read it.
//   - tenantId   ← in-memory ref (setActiveTenantId), published by the
//     auth-store post-login. Sent as headers `x-dhk8-tenant-id` and
//     `X-Tenant-Id` (some auth services expect the spelling). The backend
//     tenantGuard (S0-2) cross-checks against JWT.tenantId — divergent
//     values → 403 TENANT_MISMATCH.
//   - userId     ← in-memory ref (setActiveUserId). Header `x-dhk8-user-id`
//     is informational only; backend extracts the canonical userId from
//     JWT.sub.
// Las cookies legacy `dhk8_tenant` / `dhk8_user` fueron eliminadas — JS no
// las escribe ni las lee.

import { apiBase } from './api-origin.js'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// ── Active tenant context (test/live "Option A") ──────────────────────────
// An Account owns a liveTenantId + a testTenantId; the active env is decided
// client-side (auth-store). Everything that calls getTenantId() (every client,
// store and hook) transparently picks up whichever tenant is active. The
// auth-store pushes the resolved value here via setActiveTenantId().
//
// S1-Y hardening 2026-05-24: dropped the document.cookie fallback for
// `dhk8_tenant` / `dhk8_user`. The values now live ONLY in this module's
// in-memory ref (set by the auth-store after pin-login / email-login /
// hydrate-via-/v1/auth/me). The backend's tenantGuard (S0-2) validates that
// JWT.tenantId matches query/header, so even an exfiltrated stale cookie
// could not have crossed tenants; eliminating the cookies removes the XSS
// surface entirely (audit-bleeding C28 follow-up).
//
// The bearer JWT lives in the HttpOnly `dhk8_token` cookie set by the
// backend (sdk-auth/setAuthCookie). JS can't read it; the browser
// auto-attaches it via `credentials:'include'` on every fetch below — so
// we no longer build the `Authorization` header from document.cookie.
let activeTenantOverride: string | null = null
let activeUserOverride: string | null = null

/**
 * S1-Y hardening 2026-05-24: in-memory pub/sub para que `useTenantId` (y
 * cualquier otro consumer) se entere de cambios al activeTenantId sin
 * tener que pollear `document.cookie`. setActiveTenantId notifica a todos
 * los listeners cuando el valor cambia.
 */
type TenantListener = (id: string | null) => void
const tenantListeners = new Set<TenantListener>()

/** Subscribe a un cambio de activeTenantId. Devuelve unsubscribe. */
export function subscribeActiveTenantId(listener: TenantListener): () => void {
  tenantListeners.add(listener)
  return () => { tenantListeners.delete(listener) }
}

/** Set the active tenantId (live or test) — called by the auth-store. Pass null/'' to clear. */
export function setActiveTenantId(id: string | null | undefined): void {
  const next = id && id.length > 0 ? id : null
  if (next === activeTenantOverride) return
  activeTenantOverride = next
  for (const fn of tenantListeners) fn(next)
}

/** Set the acting user id — called by the auth-store on login/hydrate. Pass null/'' to clear. */
export function setActiveUserId(id: string | null | undefined): void {
  activeUserOverride = id && id.length > 0 ? id : null
}

/**
 * Sucursal activa del operador — la del JWT. La fija el auth-store en
 * login/hydrate. El plano la usa para no pintar las mesas de OTRA sucursal.
 */
const BRANCH_KEY = 'dhk8-pos-branch'
let activeBranchOverride: string | null =
  (typeof localStorage !== 'undefined' ? localStorage.getItem(BRANCH_KEY) : null) || null
const branchListeners = new Set<(id: string | null) => void>()

/** Suscripción reactiva (useSyncExternalStore). */
export function subscribeActiveBranch(listener: (id: string | null) => void): () => void {
  branchListeners.add(listener)
  return () => { branchListeners.delete(listener) }
}

/** Fija la sucursal activa. Persiste: el mesero no la re-elige en cada recarga. */
export function setActiveBranchId(id: string | null | undefined): void {
  const next = id && id.length > 0 ? id : null
  if (next === activeBranchOverride) return
  activeBranchOverride = next
  try {
    if (next === null) localStorage.removeItem(BRANCH_KEY)
    else localStorage.setItem(BRANCH_KEY, next)
  } catch { /* modo privado */ }
  for (const fn of branchListeners) fn(next)
}

export function getActiveBranchId(): string | null {
  return activeBranchOverride
}

export function getTenantId(): string | null {
  return activeTenantOverride
}

function getUserId(): string | null {
  return activeUserOverride
}

// ─────────────────────────────────────────────────────────────────────────
// 2026-05-24 hotfix — JWT persist + Bearer header
//
// Por qué: el navegador DROPEA la cookie HttpOnly `dhk8_token` (SameSite=Lax)
// en fetch cross-origin entre `pos.nintaigo.com` y `api.nintaigo.com` aunque
// sean mismo eTLD+1, porque el modelo de "cross-site fetch" no incluye cookies
// Lax. Sin cookie, el backend recibe la request sin JWT → 401
// "tenant scope required — present a valid JWT" en /floor, /menu, /avisos, etc.
//
// Fix: persistir el JWT en localStorage y mandarlo siempre como
// `Authorization: Bearer` (mismo patrón que admin-shell, ver
// apps/admin-shell/src/lib/api.ts:48). La cookie sigue siendo seteada por
// el backend en paralelo (no causa daño) pero ya no dependemos de ella.
//
// Trade-off de seguridad: localStorage es XSS-readable. Riesgo aceptado
// porque (1) el POS no muestra contenido user-generated, superficie XSS
// limitada; (2) cross-origin no funciona con cookie, no hay alternativa
// sin servirlo todo desde el mismo origin. Hardening adicional pendiente
// (rotación corta, refresh httpOnly) para post-lunes.
// ─────────────────────────────────────────────────────────────────────────
const JWT_LS_KEY = 'dhk8_pos_jwt'
const REFRESH_LS_KEY = 'dhk8_pos_refresh_jwt'

export function getStoredJwt(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(JWT_LS_KEY)
  } catch {
    return null
  }
}

export function setStoredJwt(token: string | null): void {
  if (typeof window === 'undefined') return
  try {
    if (token === null || token.length === 0) {
      window.localStorage.removeItem(JWT_LS_KEY)
    } else {
      window.localStorage.setItem(JWT_LS_KEY, token)
    }
  } catch {
    /* quota / private mode */
  }
}

export function getStoredRefreshJwt(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(REFRESH_LS_KEY)
  } catch {
    return null
  }
}

export function setStoredRefreshJwt(token: string | null): void {
  if (typeof window === 'undefined') return
  try {
    if (token === null || token.length === 0) {
      window.localStorage.removeItem(REFRESH_LS_KEY)
    } else {
      window.localStorage.setItem(REFRESH_LS_KEY, token)
    }
  } catch {
    /* quota / private mode */
  }
}

/** Build a versioned URL for a dhk8-* service, e.g.
 *  svcUrl('dhk8-platform-auth', '/v1/auth/pin-login') → https://api.dockethub.app/v1/dhk8-platform-auth/v1/auth/pin-login */
export function svcUrl(service: string, path = ''): string {
  const p = path.startsWith('/') ? path : `/${path}`
  return `${apiBase()}/${service}${p}`
}

export interface ApiRequestInit extends Omit<RequestInit, 'body'> {
  body?: unknown
}

export async function apiFetch<T>(url: string, init?: ApiRequestInit): Promise<T> {
  const tenantId = getTenantId()
  const userId = getUserId()
  const rawBody = init?.body
  const body: BodyInit | null | undefined =
    rawBody === undefined || rawBody === null
      ? undefined
      : typeof rawBody === 'string' || rawBody instanceof FormData || rawBody instanceof URLSearchParams || rawBody instanceof Blob || rawBody instanceof ArrayBuffer
        ? (rawBody as BodyInit)
        : JSON.stringify(rawBody)

  // 2026-05-24 hotfix: mandar `Authorization: Bearer` desde localStorage
  // porque la cookie HttpOnly se pierde cross-origin (ver bloque "JWT persist"
  // arriba). Si el token no existe localmente (primera carga sin login), el
  // header se omite y el backend usará la cookie como fallback cuando esté
  // disponible (en same-origin dev no se rompe).
  const bearer = getStoredJwt()
  const headers: Record<string, string> = {
    ...(tenantId ? { 'x-dhk8-tenant-id': tenantId, 'X-Tenant-Id': tenantId } : {}),
    ...(userId ? { 'x-dhk8-user-id': userId } : {}),
    ...(bearer ? { 'Authorization': `Bearer ${bearer}` } : {}),
    ...(init?.headers as Record<string, string> | undefined),
  }
  // Content-Type: apiFetch es la ÚNICA fuente. Solo lo agrega cuando realmente
  // mandamos un body JSON — Fastify rechaza un POST body-less con
  // content-type ("body cannot be empty...") — y SOLO si el caller no trajo ya
  // uno (comparación case-insensitive). Si apiFetch sumaba 'Content-Type' y el
  // caller pasaba 'content-type', fetch los fusionaba en
  // "application/json, application/json" y Fastify lo rechazaba con 415
  // (p.ej. PATCH /v1/auth/me/preferences al aplicar el tema en el arranque).
  const hasContentType = Object.keys(headers).some((k) => k.toLowerCase() === 'content-type')
  if (body !== undefined && !(body instanceof FormData) && !hasContentType) {
    headers['Content-Type'] = 'application/json'
  }

  let res = await fetch(url, { ...init, headers, body, credentials: 'include' })

  // 2026-05-25 hotfix: rotación transparente del access token cuando expira
  // (TTL 15min). El refresh endpoint del backend ahora acepta refreshToken en
  // el body (cookies cross-origin no llegan). Si recibimos 401 con un
  // refreshToken disponible, intentamos rotar UNA vez y reintentar la request
  // original. Si la rotación también falla → lanzamos el 401 al caller.
  //
  // Guards anti-loop:
  //   - solo intentamos refresh si hay refreshToken Y la request NO era ella
  //     misma /auth/refresh (evita recursión).
  //   - flag `_retried` en init impide múltiples reintentos.
  if (res.status === 401 && !(init as { _retried?: boolean } | undefined)?._retried) {
    const refreshTok = getStoredRefreshJwt()
    const isRefreshCall = url.includes('/auth/refresh')
    if (refreshTok !== null && !isRefreshCall) {
      try {
        const refreshRes = await fetch(svcUrl('dhk8-platform-auth', '/api/auth/refresh'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: refreshTok }),
          credentials: 'include',
        })
        if (refreshRes.ok) {
          const rotated = (await refreshRes.json()) as
            | { ok: true; data: { token: string; refreshToken: string } }
            | { ok: false }
          if ('ok' in rotated && rotated.ok && typeof rotated.data.token === 'string') {
            setStoredJwt(rotated.data.token)
            if (typeof rotated.data.refreshToken === 'string' && rotated.data.refreshToken.length > 0) {
              setStoredRefreshJwt(rotated.data.refreshToken)
            }
            // Re-fire the original request with the rotated bearer.
            const retriedHeaders: Record<string, string> = {
              ...headers,
              'Authorization': `Bearer ${rotated.data.token}`,
            }
            res = await fetch(url, {
              ...init,
              headers: retriedHeaders,
              body,
              credentials: 'include',
              ...({ _retried: true } as Record<string, unknown>),
            })
          }
        } else {
          // Refresh token expired or revoked → clear local tokens so next call
          // returns 401 cleanly and the UI prompts a fresh login.
          setStoredJwt(null)
          setStoredRefreshJwt(null)
        }
      } catch {
        // Network failure on refresh — fall through to original 401.
      }
    }
  }

  if (!res.ok) {
    let msg = `HTTP ${res.status}`
    try {
      const data = await res.json()
      msg = data?.message ?? data?.error?.message ?? data?.error ?? msg
    } catch {
      // ignore
    }
    throw new ApiError(res.status, msg)
  }

  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

/** Unwrap the `{ ok: true, data } | { ok: false, error }` envelope some services use. */
export async function apiData<T>(url: string, init?: ApiRequestInit): Promise<T> {
  const body = await apiFetch<{ ok: true; data: T } | { ok: false; error: { code: string; message: string } } | T>(url, init)
  if (body !== null && typeof body === 'object' && 'ok' in body) {
    const env = body as { ok: true; data: T } | { ok: false; error: { code: string; message: string } }
    if (env.ok) return env.data
    throw new ApiError(400, `${env.error.code}: ${env.error.message}`)
  }
  return body as T
}

// ── Compat shim ────────────────────────────────────────────────────────────
// F6.1 cleanup: the legacy `BACKENDS.<key>` map was trimmed to the ONLY entry with a
// live consumer — `BACKENDS.auth`, read by `lib/clients/preferences.ts` for the
// `/me/preferences` GET/PATCH. The other 17 keys (accounts/orders/payments/folios/
// customers/products/inventory/invoices/cfdi/tenants/rbac/audit/floor/cashcuts/tips/
// printing/reports) and the private `v1()` helper that fed them were dead after the
// per-client `svcUrl('dhk8-<svc>', ...)` migrations (git grep: 0 references). The dead
// `*.dockethub.app` per-service subdomains are GONE. Drop this last entry once
// preferences.ts moves to `svcUrl('dhk8-platform-auth', '/v1/auth/me/preferences')`.
export const BACKENDS = {
  auth: svcUrl('dhk8-platform-auth', '/v1/auth'),
} as const
